/**
 * Shared idle detection — registers activity listeners once and notifies
 * subscribers when the user goes idle or resumes activity.
 *
 * Used by useStepTimer (to pause/resume timing) and useBehavioralTracker
 * (to count idle occurrences).
 */
import { useRef, useCallback, useEffect } from 'react';

const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const IDLE_THROTTLE_MS = 1_000;

const ACTIVITY_EVENTS = [
  'scroll',
  'click',
  'keydown',
  'mousemove',
  'touchstart',
  'touchmove',
] as const;

interface IdleCallbacks {
  onIdle: () => void;
  onActive: () => void;
}

/**
 * Registers a single set of activity event listeners and fires callbacks
 * when the user transitions between active and idle states.
 *
 * @param callbacks.onIdle  — Called once when the user goes idle (after IDLE_TIMEOUT_MS of inactivity)
 * @param callbacks.onActive — Called once when the user resumes activity after being idle
 * @param deps — Dependencies array to re-attach listeners (e.g., [currentIndex] for step changes)
 */
export function useIdleDetection(callbacks: IdleCallbacks, deps: React.DependencyList = []) {
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false);
  const lastResetRef = useRef(0);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const resetIdleTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < IDLE_THROTTLE_MS) return;
    lastResetRef.current = now;

    if (isIdleRef.current) {
      isIdleRef.current = false;
      callbacksRef.current.onActive();
    }

    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = setTimeout(() => {
      if (!isIdleRef.current) {
        isIdleRef.current = true;
        callbacksRef.current.onIdle();
      }
    }, IDLE_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    // Reset state on dependency change
    isIdleRef.current = false;
    lastResetRef.current = 0;
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    }

    // Start initial idle timer
    resetIdleTimer();

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, resetIdleTimer);
      }
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, resetIdleTimer]);

  return { resetIdleTimer };
}
